import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { 
  Trophy, TrendingUp, Music, Play, Heart, ChevronRight,
  Loader2, ArrowUp
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';
import { toast } from 'sonner';

/**
 * Creator Chart Card - Embeddable component showing an artist's top tracks
 * Used on artist profile pages
 */
export function CreatorChartCard({ 
  artistHandle, 
  artistId,
  limit = 5,
  showViewAll = true,
  compact = false 
}) {
  const { token } = useAuth();
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requestingTrack, setRequestingTrack] = useState(null);

  useEffect(() => {
    if (artistHandle || artistId) {
      fetchChart();
    }
  }, [artistHandle, artistId]);

  const fetchChart = async () => {
    try {
      setLoading(true);
      const handle = artistHandle || artistId;
      const res = await fetch(`${API}/artists/${handle}/chart?limit=${limit}`);
      
      if (res.ok) {
        const data = await res.json();
        setChartData(data);
      }
    } catch (err) {
      console.error('Failed to fetch creator chart:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestTrack = async (trackId, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!token) {
      toast.error('Sign in to request tracks');
      return;
    }

    try {
      setRequestingTrack(trackId);
      const res = await fetch(`${API}/requests/track`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ track_id: trackId })
      });

      if (res.ok) {
        toast.success('Track requested! 🎵');
        fetchChart();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Could not request track');
      }
    } catch (err) {
      toast.error('Failed to request track');
    } finally {
      setRequestingTrack(null);
    }
  };

  if (loading) {
    return (
      <Card className="glass-card p-6">
        <div className="flex items-center gap-3 mb-4">
          <Trophy className="w-5 h-5 text-yellow-500" />
          <h3 className="font-heading font-bold">Top Tracks</h3>
        </div>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      </Card>
    );
  }

  if (!chartData || chartData.chart?.length === 0) {
    return (
      <Card className="glass-card p-6">
        <div className="flex items-center gap-3 mb-4">
          <Trophy className="w-5 h-5 text-yellow-500" />
          <h3 className="font-heading font-bold">Top Tracks</h3>
        </div>
        <div className="text-center py-8 text-muted-foreground">
          <Music className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No tracks yet</p>
        </div>
      </Card>
    );
  }

  const chart = chartData.chart || [];
  const handle = chartData.artist?.handle || artistHandle;

  return (
    <Card className="glass-card p-6" data-testid="creator-chart-card">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Trophy className="w-5 h-5 text-yellow-500" />
          <h3 className="font-heading font-bold">Top {limit} Tracks</h3>
        </div>
        {showViewAll && (
          <Link to={`/charts/creator/${handle}`}>
            <Button variant="ghost" size="sm" className="text-primary">
              View All
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        )}
      </div>

      {/* Track List */}
      <div className="space-y-2">
        {chart.map((track, index) => (
          <motion.div
            key={track.track_id}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.05 }}
          >
            <div 
              className={`flex items-center gap-3 p-2 rounded-lg hover:bg-muted/30 transition-colors ${
                track.position <= 3 ? 'bg-primary/5' : ''
              }`}
            >
              {/* Position */}
              <div className="w-8 text-center flex-shrink-0">
                {track.position === 1 ? (
                  <span className="text-lg">🥇</span>
                ) : track.position === 2 ? (
                  <span className="text-lg">🥈</span>
                ) : track.position === 3 ? (
                  <span className="text-lg">🥉</span>
                ) : (
                  <span className="text-sm font-bold text-muted-foreground">
                    {track.position}
                  </span>
                )}
              </div>

              {/* Cover Art */}
              {!compact && (
                <div className="w-10 h-10 rounded overflow-hidden flex-shrink-0 bg-gradient-to-br from-primary/20 to-secondary/20">
                  {track.cover_art_url ? (
                    <img 
                      src={track.cover_art_url} 
                      alt={track.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Music className="w-4 h-4 text-muted-foreground" />
                    </div>
                  )}
                </div>
              )}

              {/* Track Info */}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{track.title}</p>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="flex items-center gap-0.5">
                    <TrendingUp className="w-3 h-3" />
                    {track.stats?.requests || 0}
                  </span>
                  <span className="flex items-center gap-0.5">
                    <Heart className="w-3 h-3" />
                    {track.stats?.likes || 0}
                  </span>
                  <span className="flex items-center gap-0.5">
                    <Play className="w-3 h-3" />
                    {(track.stats?.plays || 0).toLocaleString()}
                  </span>
                </div>
              </div>

              {/* Request Button */}
              {!compact && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="flex-shrink-0 h-8 px-2"
                  onClick={(e) => handleRequestTrack(track.track_id, e)}
                  disabled={requestingTrack === track.track_id}
                  data-testid={`request-btn-${track.track_id}`}
                >
                  {requestingTrack === track.track_id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <ArrowUp className="w-4 h-4" />
                  )}
                </Button>
              )}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Footer with scoring info */}
      <div className="mt-4 pt-3 border-t border-border text-xs text-muted-foreground text-center">
        Ranked by requests, likes & plays
      </div>
    </Card>
  );
}

export default CreatorChartCard;
